import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext } from 'react';

import { useGetActualSociety } from 'hooks/useGetActualSociety';

import menuTypes from 'constants/menuTypes';
import sizes from 'constants/sizes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetCommunicationsSectionPermissions } from 'modules/communications/hooks/useGetCommunicationSectionPermissions';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetCommunication } from 'modules/communications/hooks/useGetCommunication';
import SendCommunicationModal from 'modules/_shared/components/Modals/SendCommunicationModal';
import { SendCommunicationModalProvider } from 'modules/_shared/components/Modals/SendCommunicationModal/context/SendCommunicationModalContext';

import { useGetDocuments } from 'modules/_shared/hooks/useGetDocuments';
import { CommunicationDetailsPageContextType } from '../types/CommunicationDetailsPageContextType';
import { communicationDetailsPageInitValue } from '../constants/communicationDetailsPageInitValue';
import { useGetRecipientsData } from '../hooks/useGetRecipientsData';

const CommunicationDetailsPageContext =
  createContext<CommunicationDetailsPageContextType>(
    communicationDetailsPageInitValue
  );

export const CommunicationDetailsPageProvider: FC<{
  children: ReactNode;
}> = ({ children, ...props }) => {
  const { societyId, communicationId, view } = useParams();

  const history = useHistory();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const isCertificationView = view === 'certificacion';

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const { data: communicationData, isLoading: isLoadingCommunication } =
    useGetCommunication({
      communicationId,
    });

  const { data: recipientsData, isLoading: isLoadingRecipients } =
    useGetRecipientsData({
      communication: communicationData.communication,
    });

  const { data: attachedDocumentsData, isLoading: isLoadingAttachedDocuments } =
    useGetDocuments({
      documentIds: communicationData.communication?.attachments || [],
    });

  const { data: permissionsData } = useGetCommunicationsSectionPermissions();

  const handleNewCommunication = () => {
    store.dispatch(
      setModal(
        <SendCommunicationModalProvider size={sizes.XL}>
          <SendCommunicationModal />
        </SendCommunicationModalProvider>
      )
    );
  };

  const handleToggleInboxSection = () => {
    history.push(`/comunicaciones/${societyId}/inbox/1`);
  };

  const handleToggleSettingsSection = () => {
    history.push(`/comunicaciones/${societyId}/settings`);
  };

  const handleOpenCertification = () => {
    history.push(
      `/detalle-comunicacion/${societyId}/${communicationId}/certificacion`
    );
  };

  const handleToggleFavorite = () => {};

  return (
    <CommunicationDetailsPageContext.Provider
      value={{
        data: {
          societyName: actualSociety?.name || '',
          totalCommunications: actualSociety?.communicationsCount?.total || 0,
          totalUnreadCommunications:
            actualSociety?.communicationsCount?.unread || 0,
          communication: communicationData.communication,
          recipients: recipientsData?.recipients || [],
          attachedDocuments: attachedDocumentsData?.documents || [],
          isCertificationView,
          isAdmin: permissionsData?.isAdmin || false,
          isReadOnly: permissionsData?.isReadOnly || false,
          isLoading:
            isLoadingCommunication ||
            isLoadingRecipients ||
            isLoadingAttachedDocuments,
        },
        actions: {
          handleNewCommunication,
          handleToggleInboxSection,
          handleToggleSettingsSection,
          handleOpenCertification,
          handleToggleFavorite,
        },
      }}
    >
      {children}
    </CommunicationDetailsPageContext.Provider>
  );
};

export const useCommunicationDetailsPage = () => {
  const context = useContext(CommunicationDetailsPageContext);
  if (!context) {
    throw new Error(
      'useCommunicationDetailsPage must be used within a CommunicationDetailsPageProvider'
    );
  }
  return context;
};
